import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AdyenState,
  klarnaMerchantStatus,
  KlarnaState,
  paymentsApiQueryKeys,
  refetchRestaurantPaymentStatus,
  useAllMerchants,
  useAllShopPaymentStatus,
} from '../../../apis/payments-api';
import { MerchantDto } from '@kotipizzagroup/kotipizza-payments-api-admin-client';
import { useAllRestaurantLegalEntities, useAllRestaurants } from '../../../apis/shops-api';
import { useTranslation } from 'react-i18next';
import { WebshopShopDto } from '@kotipizzagroup/kotipizza-shops-api-admin-client';

export type ErrorGroup = {
  [key: string]: string[];
};

export type ErrorGroupWithShopExternalId = {
  errors: ErrorGroup;
  shopExternalId: string;
};

type ExtendedRestaurant = WebshopShopDto & {
  injectOrdersToS4D: boolean;
};

const requiredAdyenPaymentMethods = ['googlepay', 'mobilepay', 'visa', 'mc', 'applepay', 'ebanking_FI', 'amex'];

const validateKlarna = (
  klarnaState: KlarnaState,
  restaurant: WebshopShopDto,
  merchant: MerchantDto | undefined
): string[] => {
  const errors: string[] = [];

  if (!restaurant.hasWebSale) return errors;

  if (!merchant?.klarnaMerchantData) {
    return errors;
  } else if (!klarnaState.currentStatus) {
    errors.push(`Klarna merchant status unknown (${merchant.klarnaMerchantData?.merchantId})`);
  } else {
    if (klarnaState.currentStatus !== klarnaMerchantStatus.PAYOUTS_ENABLED) {
      errors.push(
        `Status is ${klarnaMerchantStatus[klarnaState.currentStatus]} (${merchant.klarnaMerchantData?.merchantId})`
      );
    }
  }
  return errors;
};

const validateAdyen = (
  adyenState: AdyenState,
  restaurant: WebshopShopDto,
  merchant: MerchantDto | undefined,
  isS4DInjectionEnabled: boolean
): string[] => {
  const errors: string[] = [];

  const ecomMerchantKey = Object.keys(adyenState).find((key) => key.includes('ECOM'));

  if (!ecomMerchantKey) {
    return errors;
  } else {
    const ecomMerchant = adyenState[ecomMerchantKey];

    const payoutSettings = ecomMerchant.payoutSettings;
    const payoutMissing = payoutSettings.length === 0 || !payoutSettings[0].enabled;

    if (payoutMissing && restaurant.hasWebSale) {
      errors.push('ECOM merchant payout settings missing');
    }

    const paymentMethodSettings = ecomMerchant.paymentMethodSettings;

    for (const requiredMethod of requiredAdyenPaymentMethods) {
      const method = paymentMethodSettings.find((method) => method.type === requiredMethod);

      if (!method || (!method.enabled && restaurant.hasWebSale)) {
        errors.push(`${requiredMethod} is missing or disabled`);
      }
    }
  }

  if (!isS4DInjectionEnabled) {
    // Do not check POS merchant if restaurant does not have S4D enabled. This will be removed when PFT-8583 is done
    return errors;
  }

  const posMerchantKey = Object.keys(adyenState).find((key) => key.includes('KASSA'));

  if (!posMerchantKey) {
    return errors;
  } else {
    const posMerchant = adyenState[posMerchantKey];
    const payoutSettings = posMerchant.payoutSettings;
    const payoutMissing = payoutSettings.length === 0 || !payoutSettings[0].enabled;

    if (payoutMissing) {
      errors.push('POS merchant payout settings missing');
    }
  }

  return errors;
};

export const usePaymentExceptions = (ignoreKlarnaMissing = false) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: restaurants, isLoading: isLoadingRestaurants } = useAllRestaurants();

  const { data: shopPaymentProviderStatus = [], isLoading: isLoadingShopPaymentProviderStatus } =
    useAllShopPaymentStatus();

  const { data: restaurantLegalEntities, isLoading: isLoadingRestaurantLegalEntities } =
    useAllRestaurantLegalEntities();

  const { data: merchants, isLoading: isLoadingMerchants } = useAllMerchants();

  const isLoading =
    isLoadingRestaurants ||
    isLoadingShopPaymentProviderStatus ||
    isLoadingRestaurantLegalEntities ||
    isLoadingMerchants;

  const paymentExceptions = useMemo(() => {
    if (isLoading || !restaurants || !shopPaymentProviderStatus) return null;

    const entries = shopPaymentProviderStatus
      .filter((shopPaymentStatus) => {
        const restaurant = restaurants
          .filter((r) => r.isActive)
          .find((restaurant) => restaurant.shopExternalId === shopPaymentStatus.shopExternalId);
        return !!restaurant;
      })
      .map((shopPaymentStatus) => {
        const restaurant = restaurants
          .filter((r) => r.isActive)
          .find((restaurant) => restaurant.shopExternalId === shopPaymentStatus.shopExternalId);

        if (!restaurant) {
          const restaurantNotFoundError = `Unknown restaurant id ${shopPaymentStatus.shopExternalId}`;
          return {
            restaurantName: restaurantNotFoundError,
            updatedAt: shopPaymentStatus.updatedAt,
            shopExternalId: shopPaymentStatus.shopExternalId,
            errors: {
              Restaurant: [restaurantNotFoundError],
            },
          };
        }
        const { displayName, injectOrdersToS4D, hasWebSale } = restaurant as ExtendedRestaurant;
        const merchant = merchants?.find((m) => m.shopExternalId == restaurant.shopExternalId);
        const errors: ErrorGroup = {};

        errors['Adyen'] = [];
        errors['Klarna'] = [];
        errors['Edenred'] = [];
        errors['Yritys'] = [];

        if (!merchant) {
          errors['Yritys'].push(t('Merchant missing'));
          return {
            restaurantName: displayName,
            updatedAt: shopPaymentStatus.updatedAt,
            shopExternalId: shopPaymentStatus.shopExternalId,
            errors,
          };
        }

        const klarnaErrors = validateKlarna(shopPaymentStatus.klarnaState, restaurant, merchant);
        const adyenErrors = validateAdyen(shopPaymentStatus.adyenState, restaurant, merchant, injectOrdersToS4D);

        const restaurantLegalEntity = restaurantLegalEntities?.find(
          (x) => x.shopExternalId === restaurant.shopExternalId
        );

        if (!restaurantLegalEntity) {
          errors['Yritys'].push(t('Restaurant not linked to legal entity'));
        }

        if (!merchant.adyenMerchantData && hasWebSale) {
          errors['Adyen'].push(t('{{merchantType}} merchant missing', { merchantType: 'Adyen ECOM' }));
        }

        if (
          hasWebSale &&
          merchant.adyenMerchantData &&
          merchant.adyenMerchantData.kotipizzaLegalEntityId !== restaurantLegalEntity?.legalEntityId
        ) {
          errors['Adyen'].push(
            t('Restaurant legal entity does not match {{merchantType}} merchant legal entity', { merchantType: 'ECOM' })
          );
        }

        if (injectOrdersToS4D && !merchant.adyenPOSMerchantData) {
          errors['Adyen'].push(t('{{merchantType}} merchant missing', { merchantType: 'Adyen POS' }));
        }

        if (
          merchant.adyenPOSMerchantData &&
          merchant.adyenPOSMerchantData.kotipizzaLegalEntityId !== restaurantLegalEntity?.legalEntityId
        ) {
          errors['Adyen'].push(
            t('Restaurant legal entity does not match {{merchantType}} merchant legal entity', { merchantType: 'POS' })
          );
        }

        if (!merchant.klarnaMerchantData && hasWebSale) {
          errors['Klarna'].push(t('{{merchantType}} merchant missing', { merchantType: 'Klarna' }));
        }

        if (
          hasWebSale &&
          merchant.klarnaMerchantData &&
          merchant.klarnaMerchantData.kotipizzaLegalEntityId !== restaurantLegalEntity?.legalEntityId
        ) {
          errors['Klarna'].push(
            t('Restaurant legal entity does not match {{merchantType}} merchant legal entity', {
              merchantType: 'Klarna',
            })
          );
        }

        if (!merchant.edenredMerchantData && hasWebSale) {
          errors['Edenred'].push(t('{{merchantType}} merchant missing', { merchantType: 'Edenred' }));
        }

        if (
          hasWebSale &&
          merchant.edenredMerchantData &&
          merchant.edenredMerchantData.kotipizzaLegalEntityId !== restaurantLegalEntity?.legalEntityId
        ) {
          errors['Edenred'].push(
            t('Restaurant legal entity does not match {{merchantType}} merchant legal entity', {
              merchantType: 'Edenred',
            })
          );
        }

        if (adyenErrors.length > 0) {
          errors['Adyen'].push(...adyenErrors);
        }

        if (klarnaErrors.length > 0) {
          const filteredKlarnaErrors = klarnaErrors.filter((error) => {
            if (
              (ignoreKlarnaMissing && error === 'Klarna merchant missing') ||
              error.includes('Klarna merchant status unknown')
            )
              return;
            return error;
          });
          if (filteredKlarnaErrors.length > 0) {
            errors['Klarna'].push(...filteredKlarnaErrors);
          }
        }

        return {
          restaurantName: displayName,
          updatedAt: shopPaymentStatus.updatedAt,
          shopExternalId: shopPaymentStatus.shopExternalId,
          errors,
        };
      });

    return entries.filter((entry) => Object.keys(entry.errors).length > 0);
  }, [ignoreKlarnaMissing, isLoading, merchants, restaurantLegalEntities, restaurants, shopPaymentProviderStatus, t]);

  const validRestaurantsCount = useMemo(() => {
    if (!paymentExceptions || !restaurants) return null;
    return shopPaymentProviderStatus.length - paymentExceptions.length;
  }, [paymentExceptions, restaurants, shopPaymentProviderStatus.length]);

  const refetchRestaurant = async (shopExternalId: string) => {
    await refetchRestaurantPaymentStatus(shopExternalId);
    await queryClient.invalidateQueries([paymentsApiQueryKeys.allShopPaymentStatus]);
  };

  return { paymentExceptions, validRestaurantsCount, refetchRestaurant };
};
